"use strict"

import React, { forwardRef } from "react"

import { adjustDateValue, arrayWithContents } from "../../../utils"
import { downsample, lowerBound, upperBound, set_bounds } from "./utils"
import { getXValue } from "../../resources/utils"
import {
  withTimeSeriesClassificationConfig,
  withRunSearchState,
  withRunEventLabelState,
  id_accessor,
  isLTPM,
} from "../../../../components/shared_components_registration"
import AbstractCanvas from "../AbstractCanvas"
import { withPlotSettingsConfig } from "../../../config/PlotSettingsConfigProvider"
import { getTriangleSize } from "../GraphSVGLayers/PointLayer"

class _ObservationPlotCanvas extends AbstractCanvas {
  componentDidMount() {
    this.draw()
  }

  componentDidUpdate(prevProps) {
    // Not doing any checks here because this will only be invoked if
    // shouldComponentUpdate returns true
    this.draw()
  }

  render() {
    const { innerRef, width, height, margin } = this.props
    // Use passed reference (if applicable)
    if (innerRef) this.canvas = innerRef
    return (
      <div
        style={{
          position: "absolute",
          top: `${margin.top}px`,
          left: `${margin.left}px`,
        }}
      >
        <canvas
          width={width}
          height={height}
          ref={this.canvas}
          style={{
            width: `${width}px`,
            height: `${height}px`,
          }}
        />
      </div>
    )
  }

  draw() {
    const {
      width,
      height,
      timeExtractor,
      x,
      y,
      y1,
      y2,
      layers,
      gridlines,
      plot_type,
      runEventLabels = [],
      runID,
      getRunSearchGraphData, // From RunSearchConfigProvider
      getTimeSeriesData, // From TimeSeriesClassificationConfigProvider
      downsampleObservations, // From PlotSettingsConfigProvider
      getRunEventLabels, // From RunLabelConfigProvider
    } = this.props

    const g = this.getCanvasContext(width, height, this.context)
    if (!g) {
      return
    }

    if (gridlines) {
      this.gridlineLayer(g, x, y, height, width)
    }

    if (getTimeSeriesData && !isLTPM) {
      // Skynaught annotations layer
      const annotationsData = getTimeSeriesData(x, y, timeExtractor) || []
      annotationsData.map((entry) =>
        this.filledRectangleLayer(g, entry[2], entry[0], entry[1], entry[3], entry[4], entry[5]),
      )
    }

    if (getRunSearchGraphData && !isLTPM) {
      // Run Search layer
      const uniqueRuns = new Set(
        layers.filter((trend) => trend[id_accessor]).map((trend) => trend[id_accessor]),
      )
      const layerInfo = getRunSearchGraphData(x, [...uniqueRuns])
      layerInfo &&
        this.filledRectangleLayer(g, layerInfo[0], layerInfo[1], height, layerInfo[2], layerInfo[3])
    }

    if (!isLTPM) {
      // Run Event Labels
      const labels = getRunEventLabels(x, runEventLabels, timeExtractor, runID)
      labels.map((entry) =>
        this.filledRectangleLayer(g, entry[2], entry[1], y.range()[0], entry[3], entry[4], entry[5]),
      )
    }

    const [minTime, maxTime] = x.domain()
    let barObject = {}

    for (const refLayer of layers) {
      const { trendDayOffset = 0 } = refLayer
      const getXVal = (obs) => x(getXValue(obs, timeExtractor, trendDayOffset))

      let layer, dataStart, dataEnd
      if (downsampleObservations && refLayer.style !== "box") {
        layer = downsample(refLayer, plot_type, x, timeExtractor, 1.0)
        dataStart = 0
        dataEnd = layer.data.length - 1
      } else {
        layer = refLayer
        dataStart = lowerBound(layer.data, minTime, timeExtractor, trendDayOffset)
        dataEnd = upperBound(layer.data, maxTime, timeExtractor, trendDayOffset)
      }

      const y = layer.axis2 ? y2 : y1
      g.save()
      if (layer.style === "box") {
        let time
        timeExtractor.name === "Timestamp" ? (time = "timestamp") : (time = "day")
        layer.data.forEach((data) => {
          if (!barObject[data[time]]) {
            barObject[data[time]] = { count: 1, value: 0 }
          } else {
            barObject[data[time]]["count"] += 1
          }
        })
        this.boxLayer(g, x, y, timeExtractor, layer, dataStart, dataEnd, barObject)
        layer.data.forEach((data) => {
          barObject[data[time]]["value"] += data.value
        })
      } else if (layer.style === "line" && arrayWithContents(layer.data) && layer.data.length > 1) {
        this.lineLayer(g, y, layer, dataStart, dataEnd, getXVal)
      } else if (layer.style === "linespoints") {
        /*
         * Letting any set strokeWidth take precedence, but providing a default of 1 for this style of plot.
         * Note: better distinction between point and line sizing for a plot containing both
         * */
        this.lineLayer(g, y, { ...layer, strokeWidth: layer.strokeWidth || 1 }, dataStart, dataEnd, getXVal)
        this.pointLayer(g, y, layer, dataStart, dataEnd, getXVal)
      } else {
        this.pointLayer(g, y, layer, dataStart, dataEnd, getXVal)
      }

      g.restore()
    }
  }

  lineLayer(g, y, layer, dataStart, dataEnd, getXVal) {
    const data = layer.data
    g.strokeStyle = layer.colour
    g.lineWidth = layer.strokeWidth || 3

    g.beginPath()
    if (layer.strokeModifier === "dashed") {
      g.setLineDash([4, 4])
    }
    for (let index = dataStart; index <= dataEnd; ++index) {
      const obs = data[index]
      const x_value = getXVal(obs)
      const y_value = set_bounds(y(obs.value))
      if (index === dataStart) {
        g.moveTo(x_value, y_value)
      } else {
        g.lineTo(x_value, y_value)
      }
    }
    g.stroke()
  }

  pointLayer(g, y, layer, dataStart, dataEnd, getXVal) {
    const data = layer.data
    const pointSize = layer.pointSize || 2
    g.fillStyle = layer.colour

    const SIZE_ROOT_2 = getTriangleSize(pointSize) // Used for triangle points
    if (layer.pointModifier === "triangle") {
      g.strokeStyle = layer.colour
    }
    g.beginPath()
    for (let index = dataStart; index <= dataEnd; ++index) {
      const obs = data[index]

      const px = getXVal(obs),
        py = set_bounds(y(obs.value))

      if (layer.pointModifier === "triangle") {
        g.moveTo(px, py - SIZE_ROOT_2)
        g.lineTo(px - SIZE_ROOT_2, py + SIZE_ROOT_2)
        g.lineTo(px + SIZE_ROOT_2, py + SIZE_ROOT_2)
      } else {
        g.moveTo(px, py)
        g.arc(px, py, pointSize, 0, 2 * Math.PI)
      }
    }

    g.fill()
    if (layer.pointModifier === "triangle") {
      g.stroke()
    }
  }

  boxLayer(g, x, y, timeExtractor, layer, dataStart, dataEnd, barHeightObject) {
    const data = layer.data
    let [min, max] = this.props.x.domain()
    let graphSpan = this.props.timeExtractor.difference(max, min)
    let barWidth = this.props.width / graphSpan
    g.strokeStyle = "black"
    g.lineWidth = layer.strokeWidth || 1
    g.fillStyle = layer.colour

    for (let index = dataStart; index <= dataEnd; ++index) {
      const obs = data[index]
      let xValue = timeExtractor.getObservationTime(obs)
      let nextXValue
      if (timeExtractor.name === "Timestamp") {
        nextXValue = adjustDateValue(xValue, "day", 1, true)
      } else {
        nextXValue = barWidth
      }

      g.beginPath()
      g.moveTo(x(xValue), y(obs.value + barHeightObject[obs.timestamp]["value"]))
      g.lineTo(x(nextXValue), y(obs.value + barHeightObject[obs.timestamp]["value"]))
      g.lineTo(x(nextXValue), y(barHeightObject[obs.timestamp]["value"]))
      g.lineTo(x(xValue), y(barHeightObject[obs.timestamp]["value"]))
      g.lineTo(x(xValue), y(obs.value + barHeightObject[obs.timestamp]["value"]))
      g.closePath()
      g.stroke()
      g.fill()
    }
  }
}

const ObservationPlotCanvas = withRunEventLabelState(
  withRunSearchState(withPlotSettingsConfig(withTimeSeriesClassificationConfig(_ObservationPlotCanvas))),
)

export default forwardRef((props, ref) => <ObservationPlotCanvas innerRef={ref} {...props} />)
